import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Calendar, Users, ChevronRight, ListChecks, CheckCircle, Trophy, Eye, AlertTriangle } from "lucide-react";
import api, { apiErrorMsg } from "@/lib/api";

function RankingReminder({ list }) {
  const pending = list.filter((p) => p.status === "open" && !p.ranks_filled);
  if (pending.length === 0) return null;
  return (
    <div className="bg-amber-50 border border-amber-300 rounded-sm p-4 mb-6 flex gap-3" data-testid="ranking-reminder">
      <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
      <div className="flex-1">
        <div className="font-semibold text-amber-900 text-sm">Ranking belum diisi pada {pending.length} jadwal aktif</div>
        <div className="flex flex-wrap gap-2 mt-2">
          {pending.map((p) => (
            <Link key={p.id} to={`/pengasuh/periode/${p.id}/ranking`} data-testid={`ranking-reminder-link-${p.id}`}
              className="px-2.5 py-1 bg-white border border-amber-300 hover:border-amber-500 rounded-sm text-xs font-semibold text-amber-900 transition-colors">
              {p.jenis === "mingguan" ? "Minggu" : "Bulan"} ke-{p.nomor} · {p.bulan} {p.tahun} →
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function PeriodeListPage() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/pengasuh/periode").then((r) => setList(r.data)).catch((e) => toast.error(apiErrorMsg(e))).finally(() => setLoading(false));
  }, []);

  return (
    <div className="p-4 md:p-8">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-brass-600 font-semibold mb-1">Tugas Anda</div>
        <h1 className="font-heading text-3xl font-bold text-navy-900">Jadwal Penilaian</h1>
        <p className="text-slate-500 mt-1 text-sm">Atur ranking siswa pada periode yang ditugaskan kepada Anda.</p>
      </div>

      {!loading && <RankingReminder list={list} />}

      {loading ? <div className="text-slate-500">Memuat...</div> : list.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-sm p-12 text-center">
          <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <div className="text-slate-500">Belum ada jadwal yang ditugaskan kepada Anda.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="pengasuh-periode-list">
          {list.map((p) => (
            <div key={p.id} className="bg-white border border-slate-200 rounded-sm p-5 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Calendar className="w-4 h-4 text-brass-500" />
                    <span className="font-heading font-bold text-lg text-navy-900">{p.jenis === "mingguan" ? "Minggu" : "Bulan"} ke-{p.nomor}</span>
                  </div>
                  <div className="text-sm text-slate-500">{p.bulan} {p.tahun}</div>
                </div>
                <span className={`px-2 py-0.5 text-xs font-semibold rounded-sm border ${p.status === "open" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-600 border-slate-200"}`}>
                  {p.status === "open" ? "Aktif" : "Ditutup"}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-2 mb-4">
                <div className="bg-slate-50 rounded-sm p-2 text-center">
                  <Users className="w-4 h-4 text-slate-500 mx-auto mb-0.5" />
                  <div className="font-mono font-bold text-navy-900">{p.siswa_count}</div>
                  <div className="text-[9px] uppercase text-slate-500">Serdik</div>
                </div>
                <div className="bg-slate-50 rounded-sm p-2 text-center">
                  <ListChecks className="w-4 h-4 text-blue-500 mx-auto mb-0.5" />
                  <div className="font-mono font-bold text-navy-900">{p.submitted_count}/{p.siswa_count}</div>
                  <div className="text-[9px] uppercase text-slate-500">Diisi</div>
                </div>
                <div className="bg-slate-50 rounded-sm p-2 text-center">
                  {p.ranks_filled ? <CheckCircle className="w-4 h-4 text-emerald-500 mx-auto mb-0.5" /> : <Trophy className="w-4 h-4 text-amber-500 mx-auto mb-0.5" />}
                  <div className="font-bold text-navy-900 text-xs uppercase">{p.ranks_filled ? "Ada" : "Belum"}</div>
                  <div className="text-[9px] uppercase text-slate-500">Ranking</div>
                </div>
              </div>
              <div className="flex gap-2">
                <Link to={`/pengasuh/periode/${p.id}/ranking`} className="flex-1 bg-brass-500 hover:bg-brass-600 text-white px-3 py-2 rounded-sm text-sm font-semibold flex items-center justify-center gap-2">
                  <Trophy className="w-4 h-4" /> Atur Ranking
                </Link>
                {p.ranks_filled && (
                  <Link to={`/pengasuh/periode/${p.id}/hasil`} className="px-3 py-2 border border-slate-300 hover:border-brass-500 rounded-sm text-sm font-semibold flex items-center gap-1.5"><Eye className="w-4 h-4" /> Hasil</Link>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
